import { Fragment, useState, type ReactElement } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import type { PluginVersion } from '@kirin/api-client/contracts/plugins';
import { PluginFeaturesPanel } from './PluginFeaturesPanel';
import { hasAnyPluginFeatures } from './hasAnyPluginFeatures';
import { ClickableUserCount } from './ClickableUserCount';

interface PluginVersionsTableProps {
  versions: PluginVersion[];
  /** Show a per-version Users column (org admin view). */
  showUsers?: boolean;
  /** Allow expanding a version row to inspect bundled features (org admin view). */
  showFeatures?: boolean;
  /**
   * Click handler for a version's user count. When provided, the count is
   * rendered as a `ClickableUserCount` that drills into the user-list modal
   * for that exact version (or `null` for unknown).
   */
  onUserCountClick?: (version: string | null) => void;
}

/**
 * Version detail rendered inside an expanded plugin row. Owns its own
 * features-expansion state — collapsing the parent plugin row remounts this
 * component and resets that state.
 */
export function PluginVersionsTable({
  versions,
  showUsers = false,
  showFeatures = false,
  onUserCountClick,
}: PluginVersionsTableProps): ReactElement {
  const [expandedFeatureVersions, setExpandedFeatureVersions] = useState<
    Set<string>
  >(new Set());

  if (versions.length === 0) {
    return (
      <div className="pl-8 py-4 text-xs text-muted-foreground">
        No version information available for this plugin.
      </div>
    );
  }

  const toggleFeatures = (versionKey: string): void => {
    setExpandedFeatureVersions(prev => {
      const next = new Set(prev);
      if (next.has(versionKey)) {
        next.delete(versionKey);
      } else {
        next.add(versionKey);
      }
      return next;
    });
  };

  const colCount = 1 + (showFeatures ? 1 : 0) + (showUsers ? 1 : 0) + 1;

  return (
    <div className="pl-8 py-4">
      <div className="text-sm font-medium mb-2">Versions:</div>
      <Table className="text-xs">
        <TableHeader className="border-b bg-muted">
          <TableRow className="!bg-transparent hover:!bg-transparent">
            {showFeatures && <TableHead className="w-8"></TableHead>}
            <TableHead>Version</TableHead>
            {showUsers && <TableHead className="text-center">Users</TableHead>}
            <TableHead>First Seen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map(version => {
            const key = version.version ?? '_null';
            const hasFeatures =
              showFeatures && hasAnyPluginFeatures(version.features);
            const isFeaturesExpanded = expandedFeatureVersions.has(key);
            return (
              <Fragment key={key}>
                <TableRow
                  className={`border-b ${hasFeatures ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                  onClick={hasFeatures ? () => toggleFeatures(key) : undefined}
                >
                  {showFeatures && (
                    <TableCell>
                      {hasFeatures ? (
                        isFeaturesExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )
                      ) : null}
                    </TableCell>
                  )}
                  <TableCell>
                    {version.version ?? (
                      <span className="text-muted-foreground">(unknown)</span>
                    )}
                  </TableCell>
                  {showUsers && (
                    <TableCell className="text-center">
                      {onUserCountClick ? (
                        <ClickableUserCount
                          count={version.userCount}
                          tooltipScope="on this version"
                          onClick={e => {
                            // Don't trigger features toggle when clicking the count.
                            e.stopPropagation();
                            onUserCountClick(version.version);
                          }}
                        />
                      ) : (
                        version.userCount
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-muted-foreground">
                    <RelativeTimeWithTooltip date={version.firstSeenAt} />
                  </TableCell>
                </TableRow>
                {isFeaturesExpanded && hasFeatures && version.features && (
                  <TableRow className="border-b">
                    <TableCell colSpan={colCount} className="bg-muted/30">
                      <PluginFeaturesPanel features={version.features} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

PluginVersionsTable.displayName = 'PluginVersionsTable';
